export const PRECIFICA_NO_MONITORING_LABEL = 'Sem monitoramento';
export const PRECIFICA_NO_COMPETITOR_LABEL = 'Sem concorrente';
export const PRECIFICA_NO_COMPETITORS_STATUS = 'Sem concorrentes';

export type PrecificaMinPriceStatus =
  | 'no-monitoring'
  | 'no-competitor'
  | 'available';

export type PrecificaCompetitorPrice = {
  competitorId?: string | null;
  competitor_id?: string | null;
  price?: number | string | null;
  offerPrice?: number | string | null;
  offer_price?: number | string | null;
};

export type PrecificaPriceLog = {
  status?: string | null;
  statusPrecificacao?: string | null;
  status_precificacao?: string | null;
  competitorsMinPrice?: number | string | null;
  competitors_min_price?: number | string | null;
  competitorsAndPrices?: PrecificaCompetitorPrice[] | string | null;
  s_prices?: PrecificaCompetitorPrice[] | string | null;
  createdAt?: Date | string | number | null;
  created_at?: Date | string | number | null;
};

export type PrecificaMinPriceView = {
  status: PrecificaMinPriceStatus;
  emptyLabel?: string;
  price?: number;
  competitorId?: string;
  capturedAt?: Date | string | number;
  differencePercent?: number | null;
};

function toNumber(value: number | string | null | undefined): number | null {
  if (value == null || value === '') {
    return null;
  }
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function parseCompetitors(
  value: PrecificaCompetitorPrice[] | string | null | undefined
): PrecificaCompetitorPrice[] {
  if (value == null || value === '') {
    return [];
  }
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return Array.isArray(value) ? value : [];
}

function competitorEffectivePrice(
  item: PrecificaCompetitorPrice
): number | null {
  const offerPrice = toNumber(item.offerPrice ?? item.offer_price);
  if (offerPrice != null) {
    return offerPrice;
  }
  return toNumber(item.price);
}

function getLogStatus(log: PrecificaPriceLog): string {
  return String(
    log.statusPrecificacao ?? log.status_precificacao ?? log.status ?? ''
  ).trim();
}

function isNoCompetitorsStatus(status: string): boolean {
  return status.toLowerCase() === PRECIFICA_NO_COMPETITORS_STATUS.toLowerCase();
}

function noCompetitorView(): PrecificaMinPriceView {
  return {
    status: 'no-competitor',
    emptyLabel: PRECIFICA_NO_COMPETITOR_LABEL
  };
}

type PricedCompetitor = {
  competitorId?: string;
  price: number;
};

function toPricedCompetitor(
  item: PrecificaCompetitorPrice
): PricedCompetitor | null {
  const price = competitorEffectivePrice(item);
  if (price === null) {
    return null;
  }
  const competitorId = item.competitorId ?? item.competitor_id;
  return {
    price,
    ...(typeof competitorId === 'string' && competitorId
      ? { competitorId }
      : {})
  };
}

function resolveDifferencePercent(
  subscriberPrice: number | null | undefined,
  minPrice: number
): number | null {
  const clubPrice = toNumber(subscriberPrice);
  if (clubPrice == null || clubPrice === 0) {
    return null;
  }
  return ((clubPrice - minPrice) / clubPrice) * 100;
}

/**
 * Reads the latest Precifica snapshot and returns the lowest competitor price
 * for the product card. Never throws — empty states are returned instead.
 */
export function resolvePrecificaMinPrice(
  log: unknown,
  subscriberPrice?: number | null
): PrecificaMinPriceView {
  if (log == null || typeof log !== 'object') {
    return {
      status: 'no-monitoring',
      emptyLabel: PRECIFICA_NO_MONITORING_LABEL
    };
  }

  const priceLog = log as PrecificaPriceLog;

  if (isNoCompetitorsStatus(getLogStatus(priceLog))) {
    return noCompetitorView();
  }

  const competitors = parseCompetitors(
    priceLog.competitorsAndPrices ?? priceLog.s_prices
  );
  if (competitors.length === 0) {
    return noCompetitorView();
  }

  const pricedCompetitors: PricedCompetitor[] = [];
  for (const item of competitors) {
    const priced = toPricedCompetitor(item);
    if (priced) {
      pricedCompetitors.push(priced);
    }
  }

  const aggregatedMin = toNumber(
    priceLog.competitorsMinPrice ?? priceLog.competitors_min_price
  );

  if (pricedCompetitors.length === 0 && aggregatedMin == null) {
    return noCompetitorView();
  }

  const lowest = pricedCompetitors.reduce<{
    competitorId?: string;
    price: number;
  } | null>(
    (current, item) =>
      current == null || item.price < current.price ? item : current,
    null
  );

  const price = aggregatedMin ?? lowest?.price;
  if (price == null) {
    return noCompetitorView();
  }

  const matchingCompetitor =
    pricedCompetitors.find((item) => item.price === price) ?? lowest;

  return {
    status: 'available',
    price,
    competitorId: matchingCompetitor?.competitorId,
    capturedAt: priceLog.createdAt ?? priceLog.created_at ?? undefined,
    differencePercent: resolveDifferencePercent(subscriberPrice, price)
  };
}
